import FeatherIcon from "feather-icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useRef, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Modal,
  Col,
  Row,
  Spinner,
  Input,
  Button,
} from "reactstrap";
import Select from "react-select";
import api from "../helpers/api";
import { formatCreatedDate, formatEnumName, countryOptions } from "../constants/utils";
import * as XLSX from "xlsx";
import FileModal from "../components/Common/importExportModal";
import VendorModal from "../components/VendorModal";
import PermissionGuard from '../components/Common/PermissionGuard';

const Vendor = () => {
  document.title = "Vendors | ArcMedix";
  
  const [vendorModal, setVendorModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [count, setCount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const tableRef = useRef(null);
  const [fileModal, setFileModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [vendorDetailsModal, setVendorDetailsModal] = useState(false);
  const [selectedVendorForDetails, setSelectedVendorForDetails] = useState(null);

  // Document upload handlers
  const handleFileChange = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      const uploadedUrls = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const publicURL = await getPresignedUrl(file);
        if (publicURL) {
          uploadedUrls.push(publicURL);
        }
      }

      setSelectedVendor(prev => ({
        ...prev,
        documents: [...(prev?.documents || []).filter(doc => doc !== ""), ...uploadedUrls]
      }));
    } catch (err) {
      setApiError("Error during upload: " + err.message);
    }
  };

  const getPresignedUrl = async (file) => {
    const formData = new FormData();
    formData.append("files", file);

    try {
      const response = await api.post("files/img-upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const presignedUrl = response.data?.response[0]?.publicUrl;
      return presignedUrl;
    } catch (err) {
      console.error("Error getting presigned URL:", err);
      return null;
    }
  };

  const removeDocument = (indexToRemove) => {
    setSelectedVendor(prev => ({
      ...prev,
      documents: prev?.documents?.filter((_, index) => index !== indexToRemove) || []
    }));
  };

  // Toggle vendor modal
  function toggleVendorModal(vendor = null) {
    setVendorModal(!vendorModal);
    setSelectedVendor(vendor || {});
    setErrors({});
    setApiError("");
  }

  // Toggle vendor details modal
  const toggleVendorDetailsModal = (vendor = null) => {
    setVendorDetailsModal(!vendorDetailsModal);
    setSelectedVendorForDetails(vendor);
  };

  const handleVendorModalClose = () => {
    setVendorModal(false);
    setSelectedVendor(null);
  };

  const handleVendorUpdate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setApiError("");
    setErrors({});

    // Validation
    const newErrors = {};
    if (!selectedVendor?.vendorName?.trim()) {
      newErrors.vendorName = "Vendor name is required";
    }
    if (!selectedVendor?.vendorType?.trim()) {
      newErrors.vendorType = "Vendor type is required";
    }
    if (!selectedVendor?.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(selectedVendor.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!selectedVendor?.phoneNumber?.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    }
    if (!selectedVendor?.address?.trim()) {
      newErrors.address = "Address is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      // Separate phoneCode and phoneNumber
      const phoneCode = selectedVendor?.phoneCode || "";
      const fullPhoneNumber = selectedVendor?.phoneNumber || "";
      const phoneNumber = fullPhoneNumber.replace(phoneCode, "");

      // Format contractExpiry to YYYY-MM-DD if it exists
      let formattedContractExpiry = selectedVendor.contractExpiry || "";
      if (formattedContractExpiry && formattedContractExpiry.includes('-')) {
        const parts = formattedContractExpiry.split('-');
        // Check if it's in DD-MM-YYYY format
        if (parts[0].length === 2 && parts[2].length === 4) {
          // Convert DD-MM-YYYY to YYYY-MM-DD
          formattedContractExpiry = `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        // If it's already in YYYY-MM-DD format, keep it as is
      }

      const vendorData = {
        vendorName: selectedVendor.vendorName,
        vendorType: selectedVendor.vendorType,
        email: selectedVendor.email,
        phoneCode: phoneCode,
        phoneNumber: phoneNumber,
        address: selectedVendor.address,
        remarks: selectedVendor.remarks || "",
        documents: selectedVendor.documents || [],
        spending: selectedVendor.spending || '',
        contractExpiry: formattedContractExpiry,
        vendorRole: selectedVendor.vendorRole || ""
      };

      if (selectedVendor?.id) {
        // Update existing vendor
        const updateData = {
          id: selectedVendor.id,
          ...vendorData
        };
        await api.put("vendor/update-Vendor", updateData);
      } else {
        // Create new vendor
        await api.post("vendor/create-Vendor", vendorData);
      }

      handleVendorModalClose();
      reloadVendors();
    } catch (error) {
      console.error("Error during vendor submission:", error);
      setApiError(error?.response?.data?.message || "An error occurred during submission");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedVendor?.id) {
        await api.delete(`vendor/delete-Vendor?id=${selectedVendor.id}`);
        reloadVendors();
        setDeleteModal(false);
      }
    } catch (error) {
      console.error("Error deleting vendor:", error);
      setApiError("Failed to delete vendor");
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setVendors([]);
    setCount(10);
    setHasMore(true);
  };

  const handleImport = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

    };
    reader.readAsArrayBuffer(file);
  };

  const handleExport = () => {
    const dataToExport =
      vendors?.map((vendor, index) => ({
        "#": index + 1,
        "Vendor Name": vendor?.vendorName || "",
        "Vendor Type": vendor?.vendorType || "",
        Email: vendor?.email || "",
        "Phone Number": vendor?.phoneNumber || "",
        Address: vendor?.address || "",
        Remarks: vendor?.remarks || "",
        "Created At": formatCreatedDate(vendor?.createdAt) || "",
      })) || [];

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vendors");
    XLSX.writeFile(
      wb,
      `vendors_export_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  const vendorTypes = ['SERVICE_PROVIDER', 'SPARE_PARTS_REMOVAL', 'OEM', 'DISTRIBUTOR'];

  const reloadVendors = useCallback(async () => {
    setLoading(true);
    setCount(10);
    try {
      const { data } = await api.get(
        `vendor/getAllVendors?search=${searchTerm}`
      );
      setVendors(data.response || []);
      setHasMore(data.response.length >= 10);
    } catch (error) {
      console.error("Error reloading vendors:", error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  const loadMoreVendors = async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const newCount = count + 10;
      const { data } = await api.get(
        `vendor/getAllVendors?search=${searchTerm}&count=${newCount}`
      );

      setVendors(data.response || []);
      setCount(newCount);
      setHasMore(data.response.length === newCount);
    } catch (error) {
      console.error("Error loading more vendors:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadVendors();
  }, [reloadVendors]);

  return (
    <div className="page-content mt-4">
      <div className="mb-4 d-flex align-items-center gap-3">
        <motion.div
          className="mb-4 d-flex align-items-center gap-3"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <PermissionGuard permission="vendor:create">
            <button
              type="button"
              className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake"
              onClick={() => {
                toggleVendorModal();
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                height={21}
                width={21}
              >
                <path d="M5.25 6.375a4.125 4.125 0 1 1 8.25 0 4.125 4.125 0 0 1-8.25 0ZM2.25 19.125a7.125 7.125 0 0 1 14.25 0v.003l-.001.119a.75.75 0 0 1-.363.63 13.067 13.067 0 0 1-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 0 1-.364-.63l-.001-.122ZM18.75 7.5a.75.75 0 0 0-1.5 0v2.25H15a.75.75 0 0 0 0 1.5h2.25v2.25a.75.75 0 0 0 1.5 0v-2.25H21a.75.75 0 0 0 0-1.5h-2.25V7.5Z" />
              </svg>
              Add Vendor
            </button>
          </PermissionGuard>
          <div
            className="ms-auto"
            style={{ minWidth: "400px", flexGrow: 1, maxWidth: "500px" }}
          >
            <Input
              type="text"
              placeholder="Search Vendors..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </motion.div>
      </div>
      
      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="card-title text-success">Vendors</h3>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-soft-info p-2"
              onClick={() => setFileModal(true)}
              title="Import/Export vendors"
            >
              <FeatherIcon icon="file-text" size={18} />
            </button>
          </div>
        </CardHeader>
        <CardBody>
          <div
            className="table-responsive"
            ref={tableRef}
            style={{
              overflowX: "auto",
              whiteSpace: "nowrap",
              maxHeight: "calc(100vh - 300px)",
              overflowY: "auto",
            }}
          >
            <table
              className="table table-hover mb-0"
              style={{
                borderSpacing: "0.1rem",
                borderCollapse: "separate",
              }}
            >
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  background: "white",
                  zIndex: 1,
                }}
              >
                <tr>
                  <th>#</th>
                  <th>Vendor Name</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {vendors?.map((vendor, ind) => (
                    <motion.tr
                      key={vendor.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <th scope="row">{ind + 1}</th>
                      <td>
                        <span
                          className="text-primary cursor-pointer hover-underline"
                          onClick={() => toggleVendorDetailsModal(vendor)}
                          style={{ cursor: 'pointer' }}
                        >
                          {vendor.vendorName}
                        </span>
                      </td>
                      <td>
                        <PermissionGuard permission="vendor:delete">
                          <span className='text-danger' role='button' onClick={() => {
                            setSelectedVendor(vendor);
                            setDeleteModal(true);
                          }}>
                            <FeatherIcon icon="trash-2" />
                          </span>
                        </PermissionGuard>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
            {loading && (
              <div className="text-center py-3">
                <Spinner color="primary" />
              </div>
            )}
            {!loading && vendors.length === 0 && (
              <div className="text-center py-3">No vendors found</div>
            )}
          </div>
          {hasMore && vendors.length > 0 && (
            <div className="text-center mt-3">
              <Button
                color="primary"
                onClick={loadMoreVendors}
                disabled={loading}
              >
                {loading ? "Loading..." : "Load More"}
              </Button>
            </div>
          )}
          {!hasMore && vendors.length > 0 && (
            <div className="text-center py-2 mt-3 text-muted">
              No more vendors to load
            </div>
          )}
        </CardBody>
      </Card>

      {/* Vendor Modal */}
      <Modal
        size="lg"
        isOpen={vendorModal}
        toggle={() => {
          toggleVendorModal();
        }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header">
            <h5 className="modal-title" id="myLargeModalLabel">
              {selectedVendor?.id ? "Edit Vendor" : "Add Vendor"}
            </h5>
            <button
              onClick={handleVendorModalClose}
              type="button"
              className="btn-close"
              data-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleVendorUpdate}>
              <Row className="mb-4">
                <label
                  htmlFor="horizontal-vendorname-input"
                  className="col-sm-3 col-form-label"
                >
                  Vendor Name
                </label>
                <Col sm={9}>
                  <input
                    type="text"
                    value={selectedVendor?.vendorName || ""}
                    onChange={(e) =>
                      setSelectedVendor({
                        ...selectedVendor,
                        vendorName: e.target.value,
                      })
                    }
                    className={`form-control ${
                      errors.vendorName ? "is-invalid" : ""
                    }`}
                    placeholder="Enter vendor name"
                    id="horizontal-vendorname-input"
                  />
                  {errors.vendorName && (
                    <small className="text-danger">{errors.vendorName}</small>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-vendortype-input"
                  className="col-sm-3 col-form-label"
                >
                  Vendor Type
                </label>
                <Col sm={9}>
                  <select
                    value={selectedVendor?.vendorType || ""}
                    onChange={(e) =>
                      setSelectedVendor({
                        ...selectedVendor,
                        vendorType: e.target.value,
                      })
                    }
                    className={`form-select ${
                      errors.vendorType ? "is-invalid" : ""
                    }`}
                    id="horizontal-vendortype-input"
                  >
                    <option value="">Select vendor type</option>
                    {vendorTypes?.map((vendorType) => (
                      <option key={vendorType} value={vendorType}>
                        {formatEnumName(vendorType)}
                      </option>
                    ))}
                  </select>
                  {errors.vendorType && (
                    <small className="text-danger">{errors.vendorType}</small>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-email-input"
                  className="col-sm-3 col-form-label"
                >
                  Email
                </label>
                <Col sm={9}>
                  <input
                    type="email"
                    value={selectedVendor?.email || ""}
                    onChange={(e) =>
                      setSelectedVendor({
                        ...selectedVendor,
                        email: e.target.value,
                      })
                    }
                    className={`form-control ${
                      errors.email ? "is-invalid" : ""
                    }`}
                    placeholder="Enter email address"
                    id="horizontal-email-input"
                  />
                  {errors.email && (
                    <small className="text-danger">{errors.email}</small>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-phone-input"
                  className="col-sm-3 col-form-label"
                >
                  Phone Number
                </label>
                <Col sm={9}>
                  <div className="d-flex gap-2">
                    {/* Country Code Select */}
                    <Select
                      options={countryOptions}
                      value={countryOptions.find((c) =>
                        selectedVendor?.phoneCode === c.dialCode.replace("+", "")
                      )}
                      onChange={(selected) => {
                        const dialCode = selected?.dialCode.replace("+", "") || "";
                        const phoneNumber = selectedVendor?.phoneNumber?.replace(/\D/g, "") || "";

                        setSelectedVendor({
                          ...selectedVendor,
                          phoneCode: dialCode,
                          phoneNumber: dialCode + phoneNumber
                        });
                      }}
                      filterOption={(option, inputValue) => {
                        if (!inputValue) return true;
                        const searchText = inputValue.toLowerCase();
                        const labelText = option.data.labelText?.toLowerCase() || '';
                        const name = option.data.name?.toLowerCase() || '';
                        const dialCode = option.data.dialCode?.toLowerCase() || '';
                        return labelText.includes(searchText) ||
                               name.includes(searchText) ||
                               dialCode.includes(searchText);
                      }}
                      className="flex-grow-0"
                      styles={{
                        control: (base) => ({
                          ...base,
                          width: "150px",
                        }),
                      }}
                      isClearable
                      placeholder="Code"
                    />

                    {/* Phone Number Input */}
                    <input
                      type="tel"
                      className={`form-control flex-grow-1 ${
                        errors.phoneNumber ? "is-invalid" : ""
                      }`}
                      value={selectedVendor?.phoneNumber?.replace(/\D/g, "").replace(selectedVendor?.phoneCode || "", "") || ""}
                      onChange={(e) => {
                        const phone = e.target.value.replace(/\D/g, "");
                        const dialCode = selectedVendor?.phoneCode || "";
                        setSelectedVendor({
                          ...selectedVendor,
                          phoneNumber: dialCode + phone
                        });
                      }}
                      placeholder="Phone number"
                      id="horizontal-phone-input"
                    />
                  </div>
                  {errors.phoneNumber && (
                    <small className="text-danger">{errors.phoneNumber}</small>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-address-input"
                  className="col-sm-3 col-form-label"
                >
                  Address
                </label>
                <Col sm={9}>
                  <textarea
                    value={selectedVendor?.address || ""}
                    onChange={(e) =>
                      setSelectedVendor({
                        ...selectedVendor,
                        address: e.target.value,
                      })
                    }
                    className={`form-control ${
                      errors.address ? "is-invalid" : ""
                    }`}
                    placeholder="Enter address"
                    id="horizontal-address-input"
                    rows={3}
                  />
                  {errors.address && (
                    <small className="text-danger">{errors.address}</small>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-remarks-input"
                  className="col-sm-3 col-form-label"
                >
                  Remarks
                </label>
                <Col sm={9}>
                  <textarea
                    value={selectedVendor?.remarks || ""}
                    onChange={(e) =>
                      setSelectedVendor({
                        ...selectedVendor,
                        remarks: e.target.value,
                      })
                    }
                    className={`form-control ${
                      errors.remarks ? "is-invalid" : ""
                    }`}
                    placeholder="Enter remarks"
                    id="horizontal-remarks-input"
                    rows={3}
                  />
                  {errors.remarks && (
                    <small className="text-danger">{errors.remarks}</small>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-spending-input"
                  className="col-sm-3 col-form-label"
                >
                  Spending
                </label>
                <Col sm={9}>
                  <input
                    type="number"
                    value={selectedVendor?.spending || ''}
                    onChange={(e) =>
                      setSelectedVendor({
                        ...selectedVendor,
                        spending: e.target.value ? parseFloat(e.target.value) : '',
                      })
                    }
                    className="form-control"
                    placeholder="Enter spending amount"
                    id="horizontal-spending-input"
                    min="0"
                    step="0.01"
                  />
                </Col>
              </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-contractExpiry-input"
                  className="col-sm-3 col-form-label"
                >
                  Contract Expiry
                </label>
                <Col sm={9}>
                  <input
                    type="date"
                    value={selectedVendor?.contractExpiry || ""}
                    onChange={(e) =>
                      setSelectedVendor({
                        ...selectedVendor,
                        contractExpiry: e.target.value,
                      })
                    }
                    className="form-control"
                    id="horizontal-contractExpiry-input"
                  />
                </Col>
              </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-vendorRole-input"
                  className="col-sm-3 col-form-label"
                >
                  Role
                </label>
                <Col sm={9}>
                  <select
                    value={selectedVendor?.vendorRole || ""}
                    onChange={(e) =>
                      setSelectedVendor({
                        ...selectedVendor,
                        vendorRole: e.target.value,
                      })
                    }
                    className={`form-select ${
                      errors.vendorRole ? "is-invalid" : ""
                    }`}
                    id="horizontal-vendorRole-input"
                  >
                    <option value="">Select role</option>
                    <option value="ROLE1">Role1</option>
                    <option value="ROLE2">Role2</option>
                    <option value="ROLE3">Role3</option>
                  </select>
                  {errors.vendorRole && (
                    <small className="text-danger">{errors.vendorRole}</small>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <label htmlFor="documentsUpload" className="col-sm-3 col-form-label">
                  Documents
                </label>
                <Col sm={9}>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.txt,.xls,.xlsx"
                    onChange={handleFileChange}
                    className="form-control"
                    id="documentsUpload"
                    multiple
                  />
                  {selectedVendor?.documents && selectedVendor.documents.filter(doc => doc !== "").length > 0 && (
                    <div className="mt-3">
                      <small className="text-muted">Current Documents:</small>
                      <div className="mt-1">
                        {selectedVendor.documents.filter(doc => doc !== "").map((docUrl, index) => {
                          const isPdf = docUrl?.toLowerCase().endsWith(".pdf");
                          const isImage = ["jpg", "jpeg", "png", "gif"].some(ext =>
                            docUrl?.toLowerCase().endsWith(ext)
                          );
                          const fileName = docUrl?.split("/").pop() || `Document ${index + 1}`;

                          return (
                            <div key={index} className="d-flex align-items-center justify-content-between p-2 border rounded mb-1">
                              <div className="d-flex align-items-center">
                                <FeatherIcon
                                  icon={isPdf ? "file-text" : isImage ? "image" : "file"}
                                  size="16"
                                  className="text-primary me-2"
                                />
                                <small className="text-truncate" style={{ maxWidth: '200px' }}>
                                  {fileName}
                                </small>
                              </div>
                              <div className="d-flex align-items-center gap-1">
                                <a
                                  href={docUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-outline-primary btn-sm"
                                  style={{ fontSize: '10px', padding: '2px 6px' }}
                                >
                                  View
                                </a>
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  style={{ fontSize: '10px', padding: '2px 6px' }}
                                  onClick={() => removeDocument(index)}
                                >
                                  <FeatherIcon icon="x" size={12} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </Col>
              </Row>

              <div className="mt-4">
                {apiError && (
                  <div className="alert alert-danger">{apiError}</div>
                )}
                <button
                  type="submit"
                  className="btn btn-success w-lg transition-all hover-grow pulse-on-hover"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Processing..."
                    : selectedVendor?.id
                    ? "Update"
                    : "Add"}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <div className="modal-header">
          <h5 className="modal-title">Confirm Delete</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setDeleteModal(false)}
            aria-label="Close"
          ></button>
        </div>
        <div className="modal-body">
          <p>
            Are you sure you want to delete this vendor? This action cannot be
            undone.
          </p>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDeleteModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDelete}
          >
            Delete
          </button>
        </div>
      </Modal>

      {/* Vendor Details Modal */}
      <VendorModal
        isOpen={vendorDetailsModal}
        toggle={toggleVendorDetailsModal}
        vendorData={selectedVendorForDetails}
        loading={false}
        onEditVendor={(vendor) => {
          toggleVendorDetailsModal();
          toggleVendorModal(vendor);
        }}
      />

      <FileModal
        title="Vendors"
        isOpen={fileModal}
        toggle={() => setFileModal(false)}
        data={vendors}
        importNeed={false}
        error={apiError}
        loading={loading}
        onImport={handleImport}
        onExport={handleExport}
        importLabel="Import Vendors from Excel"
        exportLabel="Export Vendors to Excel"
      />
    </div>
  );
};

export default Vendor;